// The ONLY module allowed to touch navigator.xr — the native WebXR `immersive-ar`
// engine (ARCore-backed on Android Chrome). Satisfies the same EngineSession
// contract as xr8Engine.ts, so scene/, placement/ and ui/ cannot tell them apart.
//
// WHY THIS EXISTS: iOS Safari has no WebXR AR, which is why the 8th Wall camera-
// pipeline binary is the engine there. That constraint has never applied to
// Android Chrome, which exposes ARCore's own SLAM through `immersive-ar` —
// better tracking, no 6.3 MB engine download, and no vendor dependency.
//
// KEY DIFFERENCES vs the 8th Wall pipeline, all absorbed inside this file:
//  · The system composites the camera feed. We render ONLY content, on a
//    transparent clear — there is no camera-feed pass of our own.
//  · The page's DOM is invisible during an immersive session unless it is
//    published as a `dom-overlay` root. We publish the canvas's parent, which in
//    both views (.share-view) is exactly the element wrapping the canvas + HUD.
//  · Screen taps arrive as XRSession 'select' events, not DOM pointer events on
//    the canvas. We re-dispatch them as `pointerdown` on the canvas so
//    arShareSession's tap-to-place works unmodified.
//  · The session can end from outside the app (system back gesture) — surfaced
//    through EngineCallbacks.onSessionEnd.
import * as THREE from 'three'
import type {EngineCallbacks, EngineSession} from './types'
import {addLighting} from './xr8Engine'

/** dom-overlay is what makes the React HUD visible during the session. Without it
 *  the AR view still works, but the user sees no HUD — so we degrade rather than
 *  fail, and report it. */
const OPTIONAL_FEATURES = ['dom-overlay']

const UI_SELECTOR = 'button, a, input, select, textarea, [data-xr-ui]'

/**
 * requestSession is documented to sometimes neither resolve nor reject on Chrome
 * Android (google/model-viewer#4765), which would strand the app in the AR phase
 * with no camera and no fallback. Bound it and let engine.ts fall back to the
 * 8th Wall path, mirroring the 15 s guard the 8th Wall boot already has.
 */
const SESSION_REQUEST_TIMEOUT_MS = 20000

/**
 * three's canonical pattern is ONE renderer reused across sessions. Building a
 * fresh WebGLRenderer per session would hand the second one the first one's
 * still-live canvas context with a stale state cache — the documented
 * exit-and-re-enter failure (black screen / degraded tracking, pmndrs/xr#490).
 * Keyed weakly so it dies with the canvas.
 */
const renderers = new WeakMap<HTMLCanvasElement, THREE.WebGLRenderer>()

function getRenderer(canvas: HTMLCanvasElement): THREE.WebGLRenderer {
  const existing = renderers.get(canvas)
  if (existing) return existing
  const renderer = new THREE.WebGLRenderer({canvas, alpha: true, antialias: true})
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.outputColorSpace = THREE.SRGBColorSpace
  // The camera feed is composited by the system behind our framebuffer; anything
  // we clear opaquely would paint over the real world.
  renderer.setClearAlpha(0)
  renderer.xr.enabled = true
  renderers.set(canvas, renderer)
  return renderer
}

/** Reject a promise that never settles, without leaving the original dangling. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`timed out after ${ms} ms`)), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      },
    )
  })
}

export type XrRefSpace = 'local-floor' | 'unbounded'

/**
 * Which reference space to track in. Default `local-floor` — but be aware it is
 * a MISNOMER on Chrome Android: Chromium hardcodes the floor at 1.2 m below the
 * session-start camera pose and never consults ARCore's plane detection, so it
 * is a guess, not a measurement. We request it anyway because that guess is a
 * far better starting point than `local` (floor at head height), and then
 * correct it with a hit-test (see applyFloorCorrection).
 *
 * `?refspace=unbounded` is an ESCAPE HATCH FOR MEASUREMENT, not a recommended
 * mode. `unbounded` is the space nominally designed for walking-scale
 * experiences, and ARCore does advertise it — but Chromium's XRSession::
 * GetMojoFrom() returns the identity transform for BOTH `local` and `unbounded`
 * (each with a TODO citing crbug.com/1070380), so on Chrome Android the two are
 * the same space and there is no drift benefit to collect. It is also NOT
 * floor-referenced, so selecting it moves y=0 up to head height and breaks the
 * app's ground-plane assumptions. Kept only so the field test can confirm this
 * on real hardware; expect it to be worse, not better.
 */
export function requestedRefSpace(): XrRefSpace {
  return new URLSearchParams(window.location.search).get('refspace') === 'unbounded'
    ? 'unbounded'
    : 'local-floor'
}

export interface WebXrSupport {
  /** navigator.xr exists at all. */
  hasXr: boolean
  /** navigator.xr.isSessionSupported('immersive-ar') resolved true. */
  arSupported: boolean
  /** Reason arSupported is false, for diagnostics. */
  reason: string | null
}

/**
 * Can this browser start an immersive-ar session? On Android Chrome this returns
 * false when Google Play Services for AR is missing or the device is not on
 * ARCore's supported list — which is precisely when we must fall back.
 */
export async function detectWebXrSupport(): Promise<WebXrSupport> {
  if (typeof navigator === 'undefined' || !navigator.xr) {
    return {hasXr: false, arSupported: false, reason: 'navigator.xr is not available'}
  }
  if (!window.isSecureContext) {
    return {hasXr: true, arSupported: false, reason: 'not a secure context (WebXR requires HTTPS)'}
  }
  try {
    const ok = await navigator.xr.isSessionSupported('immersive-ar')
    return {
      hasXr: true,
      arSupported: ok,
      reason: ok ? null : 'immersive-ar not supported (no ARCore, or unsupported device)',
    }
  } catch (err) {
    return {hasXr: true, arSupported: false, reason: `isSessionSupported threw: ${String(err)}`}
  }
}

/**
 * Boot the native WebXR AR engine on a canvas. Resolves once the session is
 * running and three.js is bound to it.
 *
 * MUST be reached from a user gesture — requestSession requires transient user
 * activation, which survives the `await`s between the Start button and here only
 * because they are all microtask-length. Any long await added upstream will
 * break this with "requestSession requires user activation".
 */
export async function startWebXrEngine(
  canvas: HTMLCanvasElement,
  callbacks: EngineCallbacks,
): Promise<EngineSession> {
  if (!navigator.xr) throw new Error('WebXR is not available in this browser.')

  const overlayRoot = canvas.parentElement ?? document.body
  const refSpace = requestedRefSpace()

  const pending = navigator.xr.requestSession('immersive-ar', {
    requiredFeatures: [refSpace],
    // hit-test is what lets us find the REAL floor — see applyFloorCorrection.
    optionalFeatures: [...OPTIONAL_FEATURES, 'hit-test'],
    domOverlay: {root: overlayRoot},
  })

  let session: XRSession
  try {
    session = await withTimeout(pending, SESSION_REQUEST_TIMEOUT_MS)
  } catch (err) {
    // NotSupportedError (no ARCore / unsupported device — note that
    // isSessionSupported() is only an Android-version check and returns true on
    // almost any phone, so THIS is the real capability gate), NotAllowedError
    // (user declined the AR prompt), InvalidStateError (no user activation),
    // or our own timeout.
    void pending.then((late) => late.end()).catch(() => {}) // don't strand a late session
    throw new Error(`Could not start AR: ${err instanceof Error ? err.message : String(err)}`)
  }

  const hasOverlay = session.domOverlayState?.type != null
  if (!hasOverlay) console.warn('[webxr] dom-overlay unavailable — the HUD will not be visible')

  const renderer = getRenderer(canvas)
  renderer.xr.setReferenceSpaceType(refSpace)
  console.info(`[webxr] reference space: ${refSpace}`)

  const scene = new THREE.Scene()
  // near plane matches the 8th Wall default closely enough that content authored
  // for one engine doesn't clip differently on the other.
  const camera = new THREE.PerspectiveCamera(60, 1, 0.05, 1000)
  addLighting(scene)

  try {
    await renderer.xr.setSession(session)
  } catch (err) {
    try {
      await session.end()
    } catch {
      // already gone
    }
    throw err
  }

  overlayRoot.classList.add('xr-presenting')
  callbacks.onCameraStatus?.('hasVideo')

  // ── Floor correction ─────────────────────────────────────────────────────
  // `local-floor` does NOT find the floor on Chrome Android. ARCore's plane
  // detection is never consulted; Chromium hardcodes
  //   constexpr float kDefaultFloorHeightEstimation = 1.2;
  // so y=0 is exactly 1.2 m below wherever the phone was when the session
  // started. Hold the phone at eye height and every ground-placed object sits
  // ~30 cm in the air — and the app has ground-plane assumptions everywhere
  // (arShareSession's reticle raycasts y=0, the geo worldGroup sits at y=0).
  //
  // So find the real floor ourselves with one hit-test and slide the reference
  // space down to meet it. Best-effort: if no plausible hit ever arrives we
  // keep the 1.2 m guess, which is no worse than doing nothing.
  let hitTestSource: XRHitTestSource | null = null
  let floorCorrected = false
  try {
    const viewerSpace = await session.requestReferenceSpace('viewer')
    hitTestSource = (await session.requestHitTestSource?.({space: viewerSpace})) ?? null
  } catch {
    console.info('[webxr] hit-test unavailable — keeping the default floor estimate')
  }

  const applyFloorCorrection = (frame: XRFrame) => {
    if (floorCorrected || !hitTestSource) return
    const base = renderer.xr.getReferenceSpace()
    if (!base) return
    const hit = frame.getHitTestResults(hitTestSource)[0]
    if (!hit) return
    const pose = hit.getPose(base)
    if (!pose) return
    // Reject walls, ceilings and tabletops: a real floor is somewhere between
    // knee and well below eye level relative to where the camera is now.
    const drop = camera.position.y - pose.transform.position.y
    if (drop < 0.5 || drop > 3) return

    const floorY = pose.transform.position.y
    floorCorrected = true
    hitTestSource.cancel()
    hitTestSource = null
    if (Math.abs(floorY) < 0.05) return // already close enough
    // Re-origin so the detected floor becomes y = 0.
    renderer.xr.setReferenceSpace(
      base.getOffsetReferenceSpace(new XRRigidTransform({x: 0, y: floorY, z: 0})),
    )
    console.info(`[webxr] floor corrected by ${floorY.toFixed(2)} m`)
  }

  // ── Input ────────────────────────────────────────────────────────────────
  // A tap on the HUD must NOT also count as a world tap (it would place content
  // behind the button the user pressed). beforexrselect fires on the DOM overlay
  // element under the finger; cancelling it suppresses the XR 'select'.
  const onBeforeXrSelect = (event: Event) => {
    const target = event.target as HTMLElement | null
    if (target?.closest?.(UI_SELECTOR)) event.preventDefault()
  }
  overlayRoot.addEventListener('beforexrselect', onBeforeXrSelect)

  // arShareSession listens for 'pointerdown' on the canvas (the 8th Wall engine
  // delivers real DOM events there). Under WebXR the canvas is not the display
  // surface, so translate the XR select into the event it already understands.
  const onSelect = () => {
    canvas.dispatchEvent(new PointerEvent('pointerdown', {bubbles: true, cancelable: true}))
  }
  session.addEventListener('select', onSelect)

  // ── Frame loop ───────────────────────────────────────────────────────────
  let lastTime = performance.now()
  let lastTracking = ''
  let disposed = false

  const reportTracking = (status: string) => {
    if (status === lastTracking) return
    lastTracking = status
    callbacks.onTrackingStatus?.(status)
  }

  renderer.setAnimationLoop((_time, frame) => {
    if (disposed) return
    const now = performance.now()
    const dt = Math.min((now - lastTime) / 1000, 0.1) // clamp frame hitches
    lastTime = now

    // Tracking health, for the HUD/debug overlay. A null viewer pose means ARCore
    // has lost tracking; emulatedPosition means it degraded to 3-DoF, at which
    // point the geo alignment is no longer trustworthy.
    const refSpace = renderer.xr.getReferenceSpace()
    if (frame && refSpace) {
      const pose = frame.getViewerPose(refSpace)
      if (!pose) reportTracking('LIMITED')
      else reportTracking(pose.emulatedPosition ? 'EMULATED' : 'NORMAL')
    }

    // Writes the frame's viewer pose into OUR camera's position/quaternion —
    // verified in three 0.169 (WebXRManager.updateUserCamera). This is what makes
    // placement/ engine-agnostic: camera.position and camera.getWorldDirection()
    // mean the same thing under both engines. render() would do it too, but we
    // need the pose BEFORE onFrame so content updates aren't a frame behind.
    renderer.xr.updateCamera(camera)
    // Needs the updated camera (its height is what rejects wall/ceiling hits),
    // and must land before onFrame so content is never placed against the
    // uncorrected floor.
    if (frame) applyFloorCorrection(frame)

    try {
      callbacks.onFrame?.(dt, camera)
    } catch (err) {
      callbacks.onError?.(err)
    }
    renderer.render(scene, camera)
  })

  // ── Teardown ─────────────────────────────────────────────────────────────
  const cleanup = () => {
    if (disposed) return
    disposed = true
    renderer.setAnimationLoop(null)
    session.removeEventListener('select', onSelect)
    overlayRoot.removeEventListener('beforexrselect', onBeforeXrSelect)
    overlayRoot.classList.remove('xr-presenting')
    hitTestSource?.cancel()
    hitTestSource = null
    // Deliberately NOT renderer.dispose() — the renderer is cached per canvas
    // and reused by the next session (see getRenderer). Scene contents are
    // owned and disposed by their builders in scene/.
  }

  // The system back gesture / "Exit AR" ends the session without going through
  // our stop() — the app must hear about it or it renders a HUD over nothing.
  session.addEventListener('end', () => {
    cleanup()
    callbacks.onSessionEnd?.()
  })

  const forward = new THREE.Vector3()

  return {
    kind: 'webxr',
    scene,
    camera,
    recenter: () => {
      // WebXR equivalent of XR8.XrController.recenter(): re-origin the tracking
      // space at the viewer's current position + yaw. Composes correctly when
      // called repeatedly because it offsets from the CURRENT space.
      const base = renderer.xr.getReferenceSpace()
      if (!base) return
      camera.getWorldDirection(forward)
      const yaw = Math.atan2(forward.x, forward.z)
      const offset = new XRRigidTransform(
        {x: camera.position.x, y: 0, z: camera.position.z},
        {x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2)},
      )
      renderer.xr.setReferenceSpace(base.getOffsetReferenceSpace(offset))
    },
    stop: () => {
      cleanup()
      void session.end().catch(() => {
        // session may already have ended
      })
    },
  }
}
